import { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'i18next'
import { FormProvider, useForm } from 'react-hook-form'

import { stepsFlow } from '@/features/proposta/entities/Comprador/steps'
import type { TClienteCompleto } from '@/features/proposta/hooks/use-orquestrar-criacao-edicao'
import { useStepManager } from '@/features/proposta/hooks/use-stepper-proposta'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import {
  type CompradorVinculoStepSchemaInput,
  type CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { StepCompradorLayout } from '@/pages/proposta/etapa-comprador/cadastro-comprador/components/stepper-comprador'
import { ContatosComprador } from '@/pages/proposta/etapa-comprador/cadastro-comprador/contatos-comprador'
import { DadosRepresentanteLegalVinculo } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-representante-legal-vinculo'
import { EstadoCivilVinculo } from '@/pages/proposta/etapa-comprador/cadastro-comprador/estado-civil-vinculo'
import { VincularCompradorAoRepresentanteLegal } from '@/pages/proposta/etapa-comprador/cadastro-comprador/vincular-comprador-ao-representante-legal'
import type { TNacionalidade, TProfissao } from '@/types/cliente'

type TCadastroRepresentanteLegalFluxoIndependenteProps = {
  onCancel: () => void
  onSubmit: (data: CompradorVinculoStepSchemaOutput, isDirty: boolean) => Promise<void>
  clientesJuridicos: TPropostaSchemaInput['cadastroCompradores']['clientesJuridicos']
  compradores: TCompradoresCadastrados
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
  representanteLegalToEdit: TClienteCompleto | null
  emailsJaVinculados: Record<string, string>
}

export const CadastroRepresentanteLegalFluxoIndependente = ({
  onCancel,
  onSubmit,
  clientesJuridicos,
  compradores,
  nacionalidades,
  profissoes,
  representanteLegalToEdit,
  emailsJaVinculados,
}: TCadastroRepresentanteLegalFluxoIndependenteProps) => {
  const { schema, conditionalSteps } = stepsFlow.representanteLegal

  const form = useForm<CompradorVinculoStepSchemaInput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      emailsJaVinculados,
      tipo: 'Representante',
      tipoDoCliente: 'Contato',
      dados: {
        buscouCpf: false,
        cpf: '',
        nome: '',
        nacionalidadeId: nacionalidades[0]?.id ?? '',
        profissaoId: '',
        email: '',
        id: '',
        maiorDe18Anos: 'sim',
      },
      contato: {
        email: '',
        telefone: {
          origem: 'Brasil',
          numero: '',
          principal: true,
        },
      },
      estadoCivil: {
        status: undefined,
        tipoAssinatura: null,
        regimeCasamento: null,
        uniaoEstavel: null,
      },
      imagem: null,
      compradorVinculadoIds: [],
      mode: 'create',
    },
  })

  const isDirty = form.formState.isDirty

  const { steps, idx, onNext, onFinish, onBack, isLast } = useStepManager(
    conditionalSteps,
    () => form.getValues(),
    onCancel,
    onSubmit,
    isDirty,
    compradores,
  )

  const currentStep = steps[idx]

  useEffect(() => {
    const representante = representanteLegalToEdit?.vinculo
    if (representante) {
      form.reset({
        emailsJaVinculados,
        compradorVinculadoIds: [representante.id],
        contato: {
          email: representante.email,
          telefone: representante.telefone,
        },
        estadoCivil: {
          regimeCasamento: representante.regimeCasamento,
          uniaoEstavel: representante.uniaoEstavel,
          status: representante.estadoCivil,
          tipoAssinatura: null,
        },
        dados: {
          buscouCpf: true,
          cpf: representante.documento,
          nome: representante.nome,
          nacionalidadeId: representante.nacionalidadeId,
          profissaoId: representante.profissaoId,
          maiorDe18Anos: 'sim',
          id: representante.id,
        },
        imagem: representante.imagem,
        mode: 'edit',
        tipo: 'Representante',
        tipoDoCliente: 'Contato',
      })
    }
  }, [])

  const renderContent = () => {
    switch (currentStep) {
      case 'vincularCompradorParaRepresentanteLegal':
        return (
          <VincularCompradorAoRepresentanteLegal
            clientesJuridicos={clientesJuridicos}
            onBack={onBack}
            onNext={onNext}
          />
        )
      case 'dadosRepresentanteLegal':
        return (
          <DadosRepresentanteLegalVinculo
            compradores={compradores}
            nacionalidades={nacionalidades}
            profissoes={profissoes}
            onBack={onBack}
            onNext={onNext}
          />
        )
      case 'contatosRepresentanteLegal':
        return (
          <ContatosComprador
            titulo={t('proposta:cadastro_compradores.steps.contatos.responsavel_titulo', {
              responsavel: 'Representante Legal',
            })}
            fieldName="contato"
            isLast={isLast}
            onBack={onBack}
            onNext={onNext}
            onFinish={(value) => onFinish(value as CompradorVinculoStepSchemaOutput)}
          />
        )
      case 'estadoCivilRepresentanteLegal':
        return (
          <EstadoCivilVinculo
            fieldPath="estadoCivil"
            isLast={isLast}
            onBack={onBack}
            onNext={onNext}
            onFinish={(value) => onFinish(value as CompradorVinculoStepSchemaOutput)}
          />
        )
      default:
        return null
    }
  }

  return (
    <FormProvider {...form}>
      <StepCompradorLayout steps={steps} current={idx} onNext={onNext} onBack={onBack} onCancel={onCancel}>
        {renderContent()}
      </StepCompradorLayout>
    </FormProvider>
  )
}
